import {isFeatureEnabled} from '@github-ui/feature-flags'
import {FocusKeys} from '@primer/behaviors'
import {useFocusZone} from '@primer/react'
import {clsx} from 'clsx'
import {type RefObject, useCallback, useRef} from 'react'

import styles from './tab-nav.module.css'
import type {ViewSwitchProps} from './types'

export const TabNavViewSwitch = ({selectedView, onViewSelect, onLoadPreview, disabled}: ViewSwitchProps) => {
  const hybridEnabled = isFeatureEnabled('markdown_experience_hybrid_editor')
  const containerRef = useRef<HTMLDivElement>(null)
  const focusSelectedTab = useCallback(() => {
    const selectedTab = containerRef.current?.querySelector<HTMLElement>('[role=tab][aria-selected=true]')
    const firstTab = containerRef.current?.querySelector<HTMLElement>('[role=tab]')
    return selectedTab ?? firstTab ?? undefined
  }, [])

  const {containerRef: tabListRef} = useFocusZone({
    containerRef,
    disabled,
    bindKeys: FocusKeys.ArrowHorizontal | FocusKeys.HomeAndEnd,
    focusOutBehavior: 'wrap',
    focusInStrategy: focusSelectedTab,
    focusableElementFilter: element => element.getAttribute('role') === 'tab',
  })

  return (
    <div ref={tabListRef as RefObject<HTMLDivElement>} className={styles.viewSwitch}>
      <nav aria-label="View mode" className={styles.tabNav}>
        <div role="tablist" aria-label="View mode" className={styles.tabList}>
          <button
            type="button"
            role="tab"
            tabIndex={-1}
            aria-selected={selectedView === 'edit'}
            disabled={disabled}
            className={clsx(styles.tabNavLink, selectedView === 'edit' && styles.active)}
            onClick={() => onViewSelect?.('edit')}
          >
            Write
          </button>
          <button
            type="button"
            role="tab"
            tabIndex={-1}
            aria-selected={selectedView === 'preview'}
            disabled={disabled}
            className={clsx(styles.tabNavLink, selectedView === 'preview' && styles.active)}
            onClick={() => onViewSelect?.('preview')}
            onMouseOver={() => onLoadPreview?.()}
            onFocus={() => onLoadPreview?.()}
          >
            Preview
          </button>
          {hybridEnabled && (
            <button
              type="button"
              role="tab"
              tabIndex={-1}
              aria-selected={selectedView === 'hybrid'}
              disabled={disabled}
              className={clsx(styles.tabNavLink, selectedView === 'hybrid' && styles.active)}
              onClick={() => onViewSelect?.('hybrid')}
            >
              Hybrid
            </button>
          )}
        </div>
      </nav>
    </div>
  )
}
